import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import {
  TrendingUp,
  Layers,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  DollarSign,
  BadgeCheck,
  Clock,
} from "lucide-react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { StatCard } from "../../../components/PortfolioPerformance/stat-card";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import { GroupedBarChart } from "../../../components/PortfolioPerformance/charts/grouped-bar-chart";
import { Skeleton } from "../../../ui/skeleton";
import { formatCompactCurrency } from "../lib/format";

import {
  useNetInvestmentChannels,
  useNetInvestmentConcentration,
  useNetInvestmentDelinquency,
  useNetInvestmentGrowthHighlights,
  useNetInvestmentHeadline,
  useNetInvestmentPerformance,
  useNetInvestmentTopStates,
} from "../lib/api/net-investment";

// Delinquent Contracts: one golden ramp, light to dark, so the four series read as one family -- the days-past-due buckets as they
// get worse (1-29 / 30-89 / 90+) and Non-Accruing as the deepest step. The ramp is the product's --golden-1..4 tokens (the brand
// yellow is step 2), checked with the data-viz validator's ordinal mode: one hue, steady lightness steps, and the darkest step
// clears the card. Neighbouring steps are told apart by the stack's gaps and the legend, not by hue.
const DELINQUENCY_COLORS = {
  d1to29: "var(--golden-1)",
  d30to89: "var(--golden-2)",
  d90plus: "var(--golden-3)",
  nonAccruing: "var(--golden-4)",
} as const;

/** Every card on this page fetches its own endpoint independently (see net-investment.ts's
 *  own comment on why) -- react-query fires every hook below concurrently, so this wrapper
 *  gives each card its own loading/error state instead of one gate blocking the whole page
 *  on its slowest card. */
function CardBoundary({
  isPending,
  isError,
  error,
  skeletonClassName,
  children,
}: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  skeletonClassName: string;
  children: ReactNode;
}) {
  if (isError) {
    return (
      <div
        role="alert"
        className="border border-decline/50 bg-decline/10 px-4 py-3 text-sm text-foreground"
      >
        <p className="font-semibold">Could not load this.</p>
        <p className="mt-1 text-muted-foreground">
          {error instanceof Error ? error.message : "Unknown failure."}
        </p>
      </div>
    );
  }
  if (isPending) {
    return <Skeleton className={skeletonClassName} />;
  }
  return <>{children}</>;
}

export default function NetInvestment() {
  const headlineQ = useNetInvestmentHeadline();
  const concentrationQ = useNetInvestmentConcentration();
  const channelsQ = useNetInvestmentChannels();
  const topStatesQ = useNetInvestmentTopStates();
  const delinquencyQ = useNetInvestmentDelinquency();
  const performanceQ = useNetInvestmentPerformance();
  const growthHighlightsQ = useNetInvestmentGrowthHighlights();

  const [expandedChannels, setExpandedChannels] = useState<Record<string, boolean>>({});

  return (
    <div>
      <PageHeader
        title="Growth & Exposure"
        subtitle="Net investment, where it is concentrated, and how the book is growing: the internal and external view of size and health."
        asOfPage="net-investment"
      />

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CardBoundary
          isPending={headlineQ.isPending}
          isError={headlineQ.isError}
          error={headlineQ.error}
          skeletonClassName="h-28 w-full"
        >
          {headlineQ.data && (
            <>
              <StatCard
                label="Owned Net Investment"
                value={formatCompactCurrency(headlineQ.data.ownedNI.current)}
                icon={TrendingUp}
                sparkline={headlineQ.data.ownedNI.sparkline}
              />
              <StatCard
                label="Active Contracts"
                value={headlineQ.data.activeContracts.current.toLocaleString()}
                icon={Layers}
                trend={headlineQ.data.activeContracts.trend}
              />
            </>
          )}
        </CardBoundary>
        <CardBoundary
          isPending={concentrationQ.isPending}
          isError={concentrationQ.isError}
          error={concentrationQ.error}
          skeletonClassName="h-28 w-full"
        >
          {concentrationQ.data?.concentration.slice(0, 2).map((c) => (
            <StatCard
              key={c.label}
              label={`Exposure ${c.label}`}
              value={`${c.pctOwned.toFixed(1)}%`}
              icon={AlertTriangle}
              trend={c.trend}
              link={{ to: `/portfolio-performance${c.linkTo}` }}
              hint="of owned NI, click for detail"
              hintInline
            />
          ))}
        </CardBoundary>
      </div>

      <Panel
        className="mt-6"
        title="Net Investment Trend"
        description="Owned book, one point per day (latest snapshots)"
      >
        <CardBoundary
          isPending={headlineQ.isPending}
          isError={headlineQ.isError}
          error={headlineQ.error}
          skeletonClassName="h-64 w-full"
        >
          {headlineQ.data && (
            <TrendLineChart
              data={headlineQ.data.niTrend}
              series={[{ dataKey: "ownedNI", name: "Owned NI", color: "var(--amber)" }]}
              currency
              height={260}
            />
          )}
        </CardBoundary>
      </Panel>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="By Channel">
          <CardBoundary
            isPending={channelsQ.isPending}
            isError={channelsQ.isError}
            error={channelsQ.error}
            skeletonClassName="h-40 w-full"
          >
            {channelsQ.data && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="w-8 pb-2" />
                    <th className="pb-2">Channel</th>
                    <th className="pb-2 text-right">Contracts</th>
                    <th className="pb-2 text-right">NI</th>
                  </tr>
                </thead>
                <tbody>
                  {channelsQ.data.channels.map((c) => {
                    const expanded = !!expandedChannels[c.channel];
                    return (
                      <Fragment key={c.channel}>
                        <tr className="border-t border-border">
                          <td className="py-2">
                            <ExpandToggle
                              expanded={expanded}
                              label={c.channel}
                              onClick={() =>
                                setExpandedChannels((e) => ({ ...e, [c.channel]: !e[c.channel] }))
                              }
                            />
                          </td>
                          <td className="py-2">{c.channel}</td>
                          <td className="py-2 text-right tabular-nums">
                            {c.contracts.toLocaleString()}
                          </td>
                          <td className="py-2 text-right tabular-nums">
                            {formatCompactCurrency(c.ni)}
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan={4} className="bg-muted/20 p-0">
                              <table className="w-full text-sm">
                                <tbody>
                                  {c.offices.map((o) => (
                                    <tr key={o.office} className="border-t border-border/60">
                                      <td className="w-8 py-1.5" />
                                      <td className="py-1.5 pl-8 text-muted-foreground">
                                        {o.office}
                                      </td>
                                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                                        {o.contracts.toLocaleString()}
                                      </td>
                                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                                        {formatCompactCurrency(o.ni)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardBoundary>
        </Panel>
        <Panel title="Top 5 States by NI">
          <CardBoundary
            isPending={topStatesQ.isPending}
            isError={topStatesQ.isError}
            error={topStatesQ.error}
            skeletonClassName="h-40 w-full"
          >
            {topStatesQ.data && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="pb-2">State</th>
                    <th className="pb-2 text-right">Contracts</th>
                    <th className="pb-2 text-right">NI</th>
                  </tr>
                </thead>
                <tbody>
                  {topStatesQ.data.topStates.map((s) => (
                    <tr key={s.state} className="border-t border-border">
                      <td className="py-2">{s.state}</td>
                      <td className="py-2 text-right tabular-nums">
                        {s.contracts.toLocaleString()}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatCompactCurrency(s.ni)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBoundary>
        </Panel>
      </div>

      <Panel
        className="mt-6"
        title="Delinquent Contracts (NI)"
        description="Delinquencies (by NI) trend Year-on-Year and as of last month this year, by days-past-due buckets including non-accruing.."
      >
        <CardBoundary
          isPending={delinquencyQ.isPending}
          isError={delinquencyQ.isError}
          error={delinquencyQ.error}
          skeletonClassName="h-64 w-full"
        >
          {delinquencyQ.data && (
            <GroupedBarChart
              data={delinquencyQ.data.delinquency}
              categoryKey="year"
              series={[
                { dataKey: "d1to29", name: "1–29 days", color: DELINQUENCY_COLORS.d1to29 },
                { dataKey: "d30to89", name: "30–89 days", color: DELINQUENCY_COLORS.d30to89 },
                { dataKey: "d90plus", name: "90+ days", color: DELINQUENCY_COLORS.d90plus },
                { dataKey: "nonAccruing", name: "Non-Accruing", color: DELINQUENCY_COLORS.nonAccruing },
              ]}
              currency
              height={280}
              stacked
              segmentGap
            />
          )}
        </CardBoundary>
      </Panel>

      <Panel className="mt-6" title="Performing vs. Non-Performing (NI)">
        <CardBoundary
          isPending={performanceQ.isPending}
          isError={performanceQ.isError}
          error={performanceQ.error}
          skeletonClassName="h-40 w-full"
        >
          {performanceQ.data && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2">Year</th>
                  <th className="pb-2 text-right">Non-Perf. NI</th>
                  <th className="pb-2 text-right">Performing NI</th>
                </tr>
              </thead>
              <tbody>
                {performanceQ.data.performance.map((p) => (
                  <tr key={p.year} className="border-t border-border">
                    <td className="py-2">{p.year}</td>
                    <td className="py-2 text-right tabular-nums">
                      {formatCompactCurrency(p.nonPerformingNI)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatCompactCurrency(p.performingNI)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBoundary>
      </Panel>

      {/* Growth Story & Industry Benchmark: one card per figure, like the row at the top, each
          opening the benchmark page (new business volume, credit approval rate, and how Navitas
          compares on the metrics ELFA publishes). */}
      <Panel className="mt-6" title="Industry Benchmark">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <CardBoundary
            isPending={growthHighlightsQ.isPending}
            isError={growthHighlightsQ.isError}
            error={growthHighlightsQ.error}
            skeletonClassName="h-28 w-full"
          >
            {growthHighlightsQ.data?.growthHighlights.map((g) => (
              <StatCard
                key={g.label}
                label={g.label}
                value={g.value}
                icon={growthHighlightIcon(g.label)}
                link={{ to: "/portfolio-performance/industry-benchmark" }}
                trend={{ ...g.trend, ...(/dpd|delinq|past due/i.test(g.label) ? { goodDirection: "down" as const } : {}) }}
              />
            ))}
          </CardBoundary>
        </div>
      </Panel>
    </div>
  );
}

/** The icon for a Growth Story card, chosen from its label so the three stay in step with whatever
 *  the endpoint calls them. */
function growthHighlightIcon(label: string) {
  if (/new business/i.test(label)) return DollarSign;
  if (/approval/i.test(label)) return BadgeCheck;
  if (/dpd|delinq|past due/i.test(label)) return Clock;
  return TrendingUp;
}

/** Chevron toggle for a channel row's office drill-down -- same bordered-square
 *  button recipe used for this exact pattern elsewhere in the platform (e.g.
 *  risk-pricing-engine's Manage Targets Office/Industry rows). Local to this
 *  page since no second consumer exists yet in Portfolio Performance to
 *  justify a shared component. */
function ExpandToggle({ expanded, onClick, label }: { expanded: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
      aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
      onClick={onClick}
    >
      {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
    </button>
  );
}
